import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LEGAL_GAP, LEGAL_MAX_CHARS, legalComplete, type AdminLegalPage } from '@couch/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../api/client';
import { ADMIN_LEGAL_KEY, LEGAL_LINKS_KEY, legalApi } from '../../api/legal';

function PageEditor({ page }: { page: AdminLegalPage }) {
  const queryClient = useQueryClient();
  // Only the owner's edit is state; the field shows the saved text, else Couch's skeleton (CLAUDE.md).
  const [edit, setEdit] = useState<string | null>(null);
  const text = edit ?? page.body ?? page.template;
  const save = useMutation({
    mutationFn: () => legalApi.save(page.key, text),
    onSuccess: async () => {
      setEdit(null);
      await Promise.all([queryClient.invalidateQueries({ queryKey: ADMIN_LEGAL_KEY }), queryClient.invalidateQueries({ queryKey: LEGAL_LINKS_KEY })]);
    },
  });
  const id = `legal-${page.key}`;
  const state = page.complete ? 'Public' : page.body === null ? 'Not written yet' : `Not public yet — fill in every ${LEGAL_GAP}…⟧ gap`;
  return (
    <div className="legal-editor">
      <label htmlFor={id}>{page.title}</label>
      <textarea id={id} rows={12} maxLength={LEGAL_MAX_CHARS} value={text} spellCheck onChange={(e) => { setEdit(e.target.value); }} />
      <div className="pod-actions">
        <button type="button" disabled={save.isPending || (edit === null && page.body !== null)} onClick={() => { save.mutate(); }}>Save {page.title}</button>
        <span className={page.complete ? 'success' : 'muted'}>{state}</span>
        {page.complete && <Link to={`/legal/${page.key}`}>View</Link>}
        {edit !== null && !legalComplete(edit) && <span className="muted">This text still has gaps.</span>}
      </div>
      {save.isError && <p role="alert" className="form-error">{errorMessage(save.error)}</p>}
    </div>
  );
}

/** Admin → Legal pages (doc 63): the owner's Impressum, privacy policy and terms. */
export function LegalPanel() {
  const pages = useQuery({ queryKey: ADMIN_LEGAL_KEY, queryFn: legalApi.adminPages });
  return (
    <section className="panel legal-panel" aria-labelledby="legal-panel-title">
      <h2 id="legal-panel-title">Legal pages</h2>
      <p className="muted">
        Needed before Couch is open to everyone. Couch&apos;s starting text is only a skeleton with gaps ({LEGAL_GAP}…⟧) —
        it is not legal advice; write or check the text yourself. A page is public once it has no gaps left.
      </p>
      {pages.isPending && <p role="status">Loading…</p>}
      {pages.isError && <p role="alert" className="form-error">{errorMessage(pages.error)}</p>}
      {pages.data?.pages.map((p) => <PageEditor key={p.key} page={p} />)}
    </section>
  );
}
